import { Typography, Box } from '@mui/material'
import { CalendarInput, Input } from 'Common/Supplyvue-ui'
import { Check } from '@mui/icons-material'
import { differenceInCalendarWeeks, addWeeks } from 'date-fns'
import { CreatePlanDataTypes } from '../OperationalPlanner.types'
import { PLAN_DATA_ACTIONS } from './planDataReducer'

type HorizonType = 'plan' | 'constraining'

interface HorizonInputProps {
  // Component configuration
  horizonType: HorizonType
  label: string
  calendarLabel: string

  // Data and state
  createPlanDataObject: CreatePlanDataTypes
  createPlanData: CreatePlanDataTypes
  dispatch: React.Dispatch<any>
  isPlanCreating: boolean

  // Horizon mode state
  horizonsSelectionModeState: {
    planHorizon: string
    constrainingHorizon: string
  }
  setHorizonsSelectionModeState: (state: any) => void
  horizonSelectionMode: {
    weeks: string
    date: string
  }

  // Template management
  selectedTemplateKey: string | null
  setIsSelectedVariantEdited: (edited: boolean) => void
}

export const HorizonInput = ({
  horizonType,
  label,
  calendarLabel,
  createPlanDataObject,
  createPlanData,
  dispatch,
  isPlanCreating,
  horizonsSelectionModeState,
  setHorizonsSelectionModeState,
  horizonSelectionMode,
  selectedTemplateKey,
  setIsSelectedVariantEdited,
}: HorizonInputProps) => {
  // Get horizon-specific values and actions based on type
  const getHorizonConfig = () => {
    if (horizonType === 'plan') {
      return {
        weeks: createPlanDataObject.planHorizon,
        date: createPlanDataObject.planHorizonDate,
        selectionMode: horizonsSelectionModeState.planHorizon,
        weeksAction: PLAN_DATA_ACTIONS.SET_PLAN_HORIZON,
        dateAction: PLAN_DATA_ACTIONS.SET_PLAN_HORIZON_DATE,
        selectionModeKey: 'planHorizon' as const,
      }
    } else {
      return {
        weeks: createPlanDataObject.constrainingHorizon,
        date: createPlanDataObject.constrainingHorizonDate,
        selectionMode: horizonsSelectionModeState.constrainingHorizon,
        weeksAction: PLAN_DATA_ACTIONS.SET_CONSTRAINING_HORIZON,
        dateAction: PLAN_DATA_ACTIONS.SET_CONSTRAINING_HORIZON_DATE,
        selectionModeKey: 'constrainingHorizon' as const,
      }
    }
  }

  const config = getHorizonConfig()

  const handleWeeksInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHorizonDate = addWeeks(
      createPlanDataObject.planStartWeek ?? createPlanData.planStartWeek,
      parseInt(e.target.value)
    )

    dispatch({
      type: config.weeksAction,
      payload: parseInt(e.target.value),
    })
    dispatch({
      type: config.dateAction,
      payload: newHorizonDate,
    })

    if (selectedTemplateKey) {
      setIsSelectedVariantEdited(true)
    }
  }

  const handleDateInputChange = (date: Date) => {
    const weeks = differenceInCalendarWeeks(
      date,
      createPlanDataObject.planStartWeek ?? createPlanData.planStartWeek
    )

    dispatch({
      type: config.weeksAction,
      payload: weeks,
    })
    dispatch({
      type: config.dateAction,
      payload: date,
    })

    if (selectedTemplateKey) {
      setIsSelectedVariantEdited(true)
    }
  }

  const handleWeeksClick = () => {
    setHorizonsSelectionModeState({
      ...horizonsSelectionModeState,
      [config.selectionModeKey]: horizonSelectionMode.weeks,
    })
  }

  const handleDateClick = () => {
    setHorizonsSelectionModeState({
      ...horizonsSelectionModeState,
      [config.selectionModeKey]: horizonSelectionMode.date,
    })
  }

  const isWeeksMode = config.selectionMode === horizonSelectionMode.weeks
  const isDateMode = config.selectionMode === horizonSelectionMode.date

  return (
    <Box
      display={'flex'}
      flexDirection="row"
      marginTop={2}
      justifyContent="space-between"
    >
      <Box display="flex" alignItems="center" width="20%">
        <Typography>{label}</Typography>
      </Box>
      <Box
        marginLeft={3}
        display="flex"
        justifyContent="space-between"
        width="300px"
        position="relative"
      >
        <Box position="absolute" display={isWeeksMode ? 'block' : 'none'}>
          <Check fontSize="small" color="primary" />
        </Box>
        <Box>
          <Input
            type="number"
            slotProps={{
              htmlInput: { min: 0 },
            }}
            sx={{ width: '70px' }}
            value={config.weeks}
            onClick={handleWeeksClick}
            disabled={isPlanCreating}
            onChange={handleWeeksInputChange}
          />
        </Box>
        <Box display="flex" alignItems="center" flexDirection="column">
          <span>Weeks.</span>
          <span>Ends on</span>
        </Box>
        <Box
          sx={{
            width: '150px',
          }}
        >
          <Box position="absolute" display={isDateMode ? 'block' : 'none'}>
            <Check fontSize="small" color="primary" />
          </Box>
          <Box onClick={handleDateClick}>
            <CalendarInput
              label={calendarLabel}
              format="dd-MM-yyyy"
              onChange={handleDateInputChange}
              value={config.date}
              disabled={isPlanCreating}
              shouldDisableDate={(date) => date.getDay() !== 1}
            />
          </Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" width="20%" paddingLeft={1}></Box>
    </Box>
  )
}
